/**
 * Image Cache Utility
 * Manages image caching to avoid repeated downloads
 */

class ImageCache {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
  }

  /**
   * Get cached image URL or return original if not cached
   * @param {string} imageUrl - Original image URL
   * @returns {string} - Cached or original URL
   */
  getCachedUrl(imageUrl) {
    if (!imageUrl) return "/ICONOS%20BENEFICIOS-01.svg";
    
    // If URL already has query parameters, return as is (likely already processed)
    if (imageUrl.includes("?") && !imageUrl.includes("t=")) {
      return imageUrl;
    }
    
    // Remove any existing timestamp parameters
    const cleanUrl = imageUrl.split('?')[0];
    
    // Check if we have this image cached
    if (this.cache.has(cleanUrl)) {
      return this.cache.get(cleanUrl);
    }
    
    // Return clean URL without timestamp to allow browser caching
    this.cache.set(cleanUrl, cleanUrl);
    return cleanUrl;
  }

  /**
   * Preload an image to cache it
   * @param {string} imageUrl - Image URL to preload
   * @returns {Promise} - Promise that resolves when image is loaded
   */
  preloadImage(imageUrl) {
    if (!imageUrl) return Promise.resolve();
    
    const cleanUrl = imageUrl.split('?')[0];
    
    // If already loading, return existing promise
    if (this.loadingPromises.has(cleanUrl)) {
      return this.loadingPromises.get(cleanUrl);
    }
    
    // If already cached, resolve immediately
    if (this.cache.has(cleanUrl)) {
      return Promise.resolve();
    }
    
    // Create new loading promise
    const loadingPromise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.cache.set(cleanUrl, cleanUrl);
        this.loadingPromises.delete(cleanUrl);
        resolve();
      };
      
      img.onerror = () => {
        this.loadingPromises.delete(cleanUrl);
        reject(new Error(`Failed to load image: ${cleanUrl}`));
      };
      
      img.src = cleanUrl;
    });
    
    this.loadingPromises.set(cleanUrl, loadingPromise);
    return loadingPromise;
  }

  /**
   * Preload multiple images
   * @param {Array<string>} imageUrls - Array of image URLs to preload
   * @returns {Promise} - Promise that resolves when all images are loaded
   */
  preloadImages(imageUrls) {
    const promises = imageUrls
      .filter(url => url && typeof url === 'string')
      .map(url => this.preloadImage(url));
    
    return Promise.allSettled(promises);
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
  }

  /**
   * Get cache size
   * @returns {number} - Number of cached images
   */
  getCacheSize() {
    return this.cache.size;
  }

  /**
   * Check if image is cached
   * @param {string} imageUrl - Image URL to check
   * @returns {boolean} - Whether image is cached
   */
  isCached(imageUrl) {
    if (!imageUrl) return false;
    const cleanUrl = imageUrl.split('?')[0];
    return this.cache.has(cleanUrl);
  }
}

// Export singleton instance
export const imageCache = new ImageCache();

// Export class for testing or multiple instances
export default ImageCache;
